// The network half of artifact transit: only ciphertext moves. The key never appears here.
import { api, must } from "../api/client";
import { encryptArtifact, linkWithKey, newArtifactKey, verifyArtifact, type Verdict } from "./crypto";
import type { Wallet } from "../wallet/wallet";

/**
 * Give one vouch link its own encrypted copy (decided: one copy per vouch request): a fresh
 * key, the prepared file encrypted with it, the ciphertext PUT through the rail's presigned
 * URL for that request. Returns the link with the key in its fragment.
 */
export async function attachArtifactToLink(wallet: Wallet, claimId: string, token: string, link: string,
                                           prepared: { bytes: Uint8Array }): Promise<string> {
  const key = await newArtifactKey();
  const ct = await encryptArtifact(key.raw, prepared.bytes);
  const up = await requestUpload(wallet, claimId, token, ct.length);
  // Exactly the headers the rail signed; the browser sets Content-Length itself (a forbidden header for fetch).
  const headers = Object.fromEntries(Object.entries(up.headers).filter(([k]) => k.toLowerCase() !== "content-length"));
  const r = await fetch(up.upload_url, { method: "PUT", headers, body: ct });
  if (!r.ok) throw new Error(`The encrypted picture could not be uploaded (${r.status}).`);
  return linkWithKey(link, key.raw);
}

/** The presigned PUT for this request's own copy (POST /requests/{token}/artifact, signed by the holder). */
async function requestUpload(wallet: Wallet, claimId: string, token: string, size: number): Promise<{ upload_url: string; headers: Record<string, string> }> {
  const nonce = Array.from(globalThis.crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("");
  const jws = await wallet.signEnvelope("artifact_upload", { claim_id: claimId, holder_did: wallet.did!, size, nonce });
  const out = await must(api.POST("/requests/{token}/artifact", { params: { path: { token } }, body: { jws } }));
  return { upload_url: out.upload_url, headers: out.headers as Record<string, string> };
}

export type ArtifactResult = Verdict | { ok: false; reason: "no_key" | "gone" | "none" | "fetch_failed" };

/** The attester's side: this request's ciphertext, fetched with the token, decrypted and checked here. */
export async function openArtifact(token: string, key: Uint8Array | null): Promise<ArtifactResult & { mime?: string | null }> {
  if (!key) return { ok: false, reason: "no_key" };
  const r = await api.GET("/requests/{token}/artifact", { params: { path: { token } } });
  if (!r.data) return { ok: false, reason: r.response.status === 410 ? "gone" : r.response.status === 404 ? "none" : "fetch_failed" };
  const got = await fetch(r.data.download_url);
  if (!got.ok) return { ok: false, reason: "fetch_failed" };
  const verdict = await verifyArtifact(key, new Uint8Array(await got.arrayBuffer()), r.data.artifact_hash);
  return { ...verdict, mime: r.data.artifact_mime };
}
